import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { LoadingDialogComponent } from "@cotecna/dialogs";
import {
  DeletedSectionResult,
  DuplicatedSectionResult,
  FieldDefinitionChangeResult,
  RenamedSectionResult,
  Section,
} from "@cotecna/dynamic-view-engine";
import { throwError } from "rxjs";
import {
  PendingReorderSections,
  ReorderSections,
} from "src/app/inspection/model/pending-reorder-fields";
import { InspectionDetailsService } from "src/app/inspection/services/inspection-details.service";
import { InspectionFormService } from "src/app/inspection/services/inspection-form-service/inspection-form.service";
import { DynamicFormService } from "src/app/shared/services/dynamic-form/dynamic-form.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import { PendingDeleteField } from "../../../inspection/model/pending-delete-field";
import { PendingDeleteSection } from "../../../inspection/model/pending-delete-section";
import { PendingDuplicateNaturalField } from "../../../inspection/model/pending-duplicate-natural-field";
import { PendingDuplicateNaturalSection } from "../../../inspection/model/pending-duplicate-natural-section";
import { PendingRenameField } from "../../../inspection/model/pending-rename-field";
import { FormDefinitionService } from "../../../inspection/services/form-definition.service";
import { ItemFieldChangeCommand } from "../../services/commands/items-field-change.command";
import { ItemsService } from "../../services/items.service";
import { ItemFormQuery } from "../../services/queries/item-form.query";
import {
  DynamicFormActionsBase,
  FormFieldChange,
} from "./dynamic-form-actions-base";

@Component({
  selector: "im-item-form",
  templateUrl: "./item-form.component.html",
  styleUrls: ["./item-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ItemFormComponent
  extends DynamicFormActionsBase
  implements OnInit, OnChanges, OnDestroy
{
  public isLoading: boolean = true;
  private duplicatingSection = false;
  private isDestroyed = false;

  @Output()
  public goBackEvent: EventEmitter<void> = new EventEmitter<void>();

  public constructor(
    activatedRoute: ActivatedRoute,
    public formDefinitionService: FormDefinitionService,
    public inspectionFormService: InspectionFormService,
    public inspectionDetailsService: InspectionDetailsService,
    public dynamicFormService: DynamicFormService,
    ngZone: NgZone,
    private readonly itemsService: ItemsService,
    private readonly userRolePermissionsService: UserRolePermissionsService,
    private readonly dialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super(
      activatedRoute,
      formDefinitionService,
      inspectionFormService,
      inspectionDetailsService,
      dynamicFormService,
      ngZone,
    );
  }

  public inspectionFromExternalSource: boolean = false;

  public ngOnInit(): void {
    if (this.route.snapshot.params?.id) {
      this.inspectionFromExternalSource = true;
    }
  }

  public ngOnChanges() {
    this.initItemForm();
  }

  public ngOnDestroy() {
    this.isDestroyed = true;
    this.unsubscribeToFormEvents();
    this.form = null;
    this.item = null;
  }

  public goBack() {
    this.goBackEvent.next();
  }

  public async onFormFieldChanges(formFieldChanges: FormFieldChange[]) {
    for (const formFieldChange of formFieldChanges) {
      this.itemsService.applyItemFieldChange(this.values, formFieldChange);

      if (!this.creationMode()) {
        while (this.duplicatingSection) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        await this.itemsService.invokeCommand(
          new ItemFieldChangeCommand(formFieldChange),
        );
      }
    }
  }

  public async onSectionRenamed(result: RenamedSectionResult) {
    try {
      if (!this.creationMode()) {
        await this.formDefinitionService.renameSection({
          inspectionId: this.inspectionId,
          itemId: this.item.id,
          sectionId: result.sectionId,
          name: result.newName,
        });
      }
    } catch {
      this.sections.filter((itm) => itm.name == result.newName)[0].name =
        result.oldName;
    }
    const sectionToRename = this.item.form.sections.find(
      (section) => section.id === result.sectionId,
    );
    if (sectionToRename) sectionToRename.name = result.newName;
  }

  private async onSectionsReordered(result: ReorderSections) {
    try {
      const pendingReorderSections = {
        inspectionId: this.inspectionId,
        itemId: this.item.id,
        sections: result.sections,
        sectionParentId: result.sectionParentId,
      } as PendingReorderSections;

      await this.formDefinitionService.reorderSections(pendingReorderSections);
    } catch (err) {
      console.error("on section reordered", err);
      throw err;
    }
  }

  public async onSectionDuplicated(result: DuplicatedSectionResult) {
    if (!this.creationMode()) {
      const loadingDialogRef = this.dialog.open(LoadingDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: "Duplicating Section",
      });
      this.duplicatingSection = true;

      await this.formDefinitionService.duplicateNaturalSection({
        inspectionId: this.inspectionId,
        itemId: this.item.id,
        duplicatedSection: result.sectionDuplicated,
        duplicatedControls: result.duplicatedControls,
      } as PendingDuplicateNaturalSection);

      this.duplicatingSection = false;
      loadingDialogRef.close();

      await this.reorderChildSections(
        result.sectionDuplicated.duplicated.parentId,
        result.currentSections,
      );
    }
  }

  public async onSectionDeleted(result: DeletedSectionResult) {
    if (!this.creationMode()) {
      await this.formDefinitionService.deleteSection({
        inspectionId: this.inspectionId,
        itemId: this.item.id,
        sectionId: result.sectionRemoved.id,
      } as PendingDeleteSection);

      await this.reorderChildSections(
        result.sectionRemoved.duplicated.parentId,
        result.currentSections,
      );
    }
  }

  private async reorderChildSections(parentId: string, sections: Section[]) {
    const sectionsToReorder: ReorderSections = {
      sectionParentId: parentId,
      sections: [],
    };

    sections.forEach((section) => {
      if (section.duplicated?.parentId === parentId) {
        sectionsToReorder.sections.push({
          sectionId: section.id,
          order: section.order,
        });
      }
    });

    if (sectionsToReorder.sections.length > 0) {
      await this.onSectionsReordered(sectionsToReorder);
    }
  }

  public onFieldRenamed(result: FieldDefinitionChangeResult) {
    for (let i = 0; i < result.changedFieldsSections.length; i++) {
      if (!this.creationMode()) {
        this.formDefinitionService.renameField({
          inspectionId: this.inspectionId,
          itemId: this.item.id,
          sectionId: result.changedFieldsSections[i].id,
          fieldId: result.changedFields[i].id,
          name: result.changedFields[i].label,
        } as PendingRenameField);
      }
    }
  }

  public onFieldDuplicated(result: FieldDefinitionChangeResult) {
    for (let i = 0; i < result.changedFieldsSections.length; i++) {
      if (!this.creationMode()) {
        this.formDefinitionService.duplicateNaturalField({
          inspectionId: this.inspectionId,
          itemId: this.item.id,
          sectionId: result.changedFieldsSections[i].id,
          duplicatedField: result.changedFields[i],
          duplicatedControl: result.changedControls[i],
        } as PendingDuplicateNaturalField);
      }
    }
  }

  public onFieldDeleted(result: FieldDefinitionChangeResult) {
    for (let i = 0; i < result.changedFieldsSections.length; i++) {
      if (!this.creationMode()) {
        this.formDefinitionService.deleteField({
          inspectionId: this.inspectionId,
          itemId: this.item.id,
          sectionId: result.changedFieldsSections[i].id,
          fieldId: result.changedFields[i].id,
        } as PendingDeleteField);
      }
    }
  }

  public onFullPictureRequested(fieldId: string) {
    if (!this.creationMode()) {
      this.getFullPictureSubscription = this.inspectionFormService
        .onFullPictureRequested(this.inspectionId, this.item.id, fieldId)
        .subscribe(
          (result) => {
            if (result) this.openPictureTab(result);
            this.getFullPictureSubscription.unsubscribe();
          },
          (err) => throwError(err),
        );
    }
  }

  private async initItemForm() {
    this.isReadOnly = this.userRolePermissionsService.isReadOnly();
    this.setReadonlyState();
    this.isLoading = true;
    this.unsubscribeToFormEvents();
    this.resetFormValues();

    if (!this.creationMode()) {
      this.subscribeToInspectionCommonData();
      this.subscribeToFormData();
      this.itemsService.invokeQuery(
        new ItemFormQuery(this.inspectionId, this.item.id),
      );
      this.isLoading = false;
    } else {
      await this.initForm(
        this.item.form.sections,
        this.item.form.controls,
        this.item.values,
      );
    }
  }

  private creationMode(): boolean {
    return this.inspectionId == null || this.inspectionFromExternalSource;
  }

  private subscribeToFormData() {
    this.formDataSubscription = this.itemsService.itemFormData.subscribe(
      async (formData) => {
        if (formData?.itemId === this.item.id) {
          await this.initForm(
            [...(formData?.form.sections ?? [])],
            [...(formData?.form.controls ?? [])],
            [...(formData?.values ?? [])],
          );
          this.changeDetectorRef.detectChanges();
        }
      },
    );
  }

  protected override onFormInitialized(): void {
    if (!this.isDestroyed) {
      this.changeDetectorRef.detectChanges();
    }
  }
}
